// GA4 events for eagleharboradvisors.com. One helper per event, all named in
// src/Constants/analytics-taxonomy.json, which the successor site carries verbatim so the
// weekly report reads the same event names before and after cutover.
//
// Nothing personal ever goes to GA: the taxonomy lists the only parameter keys an event may
// carry, and sendEvent drops everything else, so a helper cannot forward a form field by
// accident. Page views are not sent from here; gtag's config and the stream's enhanced
// measurement own those (see src/App.js).
import ReactGA from 'react-ga4'
import taxonomy from '../Constants/analytics-taxonomy.json'

export type EventName = keyof typeof taxonomy.events
export type EventParams = Record<string, string | number | undefined | null>

export const EVENT_NAMES = Object.keys(taxonomy.events) as EventName[]

// GA4 truncates event parameter values at 100 characters; trimming here keeps a long URL
// from being silently cut mid-path.
const PARAM_MAX = 100

export const isAnalyticsEnabled = () => Boolean(process.env.REACT_APP_GOOGLE_TAG_ID)

const pagePath = () => (typeof window === 'undefined' ? '' : window.location.pathname)

export const sendEvent = (name: EventName, params: EventParams = {}): boolean => {
  if (!isAnalyticsEnabled()) return false
  const allowed: readonly string[] = taxonomy.events[name]
  const clean: Record<string, string | number> = {}
  for (const key of allowed) {
    const value = params[key]
    if (value === undefined || value === null || value === '') continue
    clean[key] = typeof value === 'number' ? value : String(value).slice(0, PARAM_MAX)
  }
  ReactGA.event(name, clean)
  return true
}

export type AppointmentCtaLocation = 'home_hero' | 'page_hero' | 'floating' | 'faq'
export type ContactCtaLocation = 'footer' | 'mobile_drawer'
export type NavLocation = 'header' | 'footer' | 'mobile'
export type FormName = 'contact' | 'risk_profiler' | 'seminar_registration' | 'seminar_waitlist'
export type ExternalLinkLabel =
  | 'client_portal'
  | 'facebook'
  | 'instagram'
  | 'twitter'
  | 'linkedin'
  | 'brokercheck'
  | 'finra'
  | 'kestra_disclosures'
  | 'youtube'
  | 'nav_external'

export const trackAppointmentModalOpened = (ctaLocation: AppointmentCtaLocation) =>
  sendEvent('appointment_modal_opened', { cta_location: ctaLocation })

export const trackCalendlyScheduled = (advisor: string) =>
  sendEvent('calendly_scheduled', { advisor })

export const trackContactModalOpened = (ctaLocation: ContactCtaLocation) =>
  sendEvent('contact_modal_opened', { cta_location: ctaLocation })

export const trackContactSubmitted = () => sendEvent('contact_submitted')
export const trackContactError = () => sendEvent('contact_error')

export const trackRiskProfilerStarted = () => sendEvent('risk_profiler_started')
export const trackRiskProfilerCompleted = (profileType: string) =>
  sendEvent('risk_profiler_completed', { profile_type: profileType })

export const trackExternalLinkClick = (linkLabel: ExternalLinkLabel, linkUrl: string) =>
  sendEvent('external_link_click', { link_label: linkLabel, link_url: linkUrl })

export const trackNavClick = (linkLabel: string, navLocation: NavLocation) =>
  sendEvent('nav_click', { link_label: linkLabel, nav_location: navLocation })

export const trackCtaClick = (buttonLabel: string) =>
  sendEvent('cta_click', { button_label: buttonLabel, page_path: pagePath() })

export const trackScrollDepth = (percent: number) =>
  sendEvent('scroll_depth', { percent, page_path: pagePath() })

export const trackFormStart = (formName: FormName) =>
  sendEvent('form_start', { form_name: formName, page_path: pagePath() })

// form_start is about a visitor reaching a field, not pressing a button inside the form.
export const isFieldFocus = (e: { target: EventTarget | null }): boolean =>
  /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement | null)?.tagName ?? '')

export type SeminarEvent =
  | 'seminar_registration_submitted'
  | 'seminar_registration_success'
  | 'seminar_registration_error'
  | 'seminar_waitlist_opened'
  | 'seminar_waitlist_joined'
  | 'seminar_waitlist_error'

// Audience rides along so GA can split medical from retirement conversions by channel.
export const trackSeminar = (action: SeminarEvent, audience?: string) =>
  sendEvent(action, { event_category: 'seminar', audience })

// "https://calendly.com/andrew-wilkins1?hide_gpdr_banner=1" -> "andrew-wilkins1". Only the
// account slug is reported, never the event type or any query the booking carried.
export const calendlyAdvisor = (calendlyUrl: string): string => {
  try {
    const first = new URL(calendlyUrl).pathname.split('/').filter(Boolean)[0]
    return first || 'unknown'
  } catch {
    return 'unknown'
  }
}

export const CALENDLY_ORIGIN = 'https://calendly.com'

// Calendly's embed posts {event: 'calendly.event_scheduled'} to the parent window. Only
// messages from Calendly's own origin count, so a stray postMessage cannot mint a booking.
export const isCalendlyScheduledMessage = (event: { origin: string; data: unknown }): boolean =>
  event.origin === CALENDLY_ORIGIN &&
  typeof event.data === 'object' &&
  event.data !== null &&
  (event.data as { event?: unknown }).event === 'calendly.event_scheduled'
